import { NodeDetails, RiskSeverity } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';

export enum NonAuthenticatedRoute {
  LOGIN = '/login',
  LOGIN_SSO = '/login-sso',
  SIGNUP = '/signup',
  FORGOT_PASSWORD = '/forgot-password',
  MARKETPLACE_AWS = '/marketplace/aws',
  MARKETPLACE_AZURE = '/marketplace/azure',
  HELLO = '/hello',
  GOODBYE = '/goodbye',
  COLLABORATOR_CHECK = '/collaborator-check',
  FEATUREBASE_SSO = '/featurebase-sso',
  SSO_AUTO = '/sso/:domain',
}

export enum AuthenticatedRoute {
  INSIGHTS = 'insights',
  SEEDS = 'seeds',
  LOGOUT = 'logout',
  ASSETS = 'assets',
  VULNERABILITIES = 'vulnerabilities',
  ATTACKS = 'attacks',
  VALIDATIONS = 'validations',
  JOBS = 'jobs',
  SETTINGS = 'settings',
  SETTINGS_SCAN = 'settings/scan',
  SETTINGS_NOTIFICATIONS = 'settings/notifications',
  SETTINGS_ORGANIZATION = 'settings/organization',
  SETTINGS_USER_MANAGEMENT = 'settings/user-management',
  SETTINGS_ACCESS_LOGS = 'settings/access-logs',
  // User Profile is a separate page, not under Settings
  PROFILE = 'profile',
  CUSTOMER_MANAGEMENT_USER_SETTINGS = 'customer-management/settings/user',
  SETTINGS_MONITORING = 'settings/monitoring',
  SETTINGS_SUBSCRIPTION = 'settings/subscription',
  FILES = 'files',
  INTEGRATIONS = 'integrations',
  USERS = 'users',
  THREATS = 'threats',
  TECHNOLOGIES = 'technologies',
  AGENTS = 'agents',
  CAPABILITIES = 'capabilities',
  CUSTOMER_MANAGEMENT = 'customer-management',
  RED_TEAM = 'red-team',
}

export const AuthenticatedAppRouteTitle: Record<AuthenticatedRoute, string> = {
  [AuthenticatedRoute.INSIGHTS]: 'Insights',
  [AuthenticatedRoute.SETTINGS]: 'Settings',
  [AuthenticatedRoute.SETTINGS_SCAN]: 'Scan Settings',
  [AuthenticatedRoute.SETTINGS_NOTIFICATIONS]: 'Notification Settings',
  [AuthenticatedRoute.SETTINGS_ORGANIZATION]: 'Organization Settings',
  [AuthenticatedRoute.SETTINGS_USER_MANAGEMENT]: 'User Management',
  [AuthenticatedRoute.SETTINGS_ACCESS_LOGS]: 'Access Logs',
  [AuthenticatedRoute.PROFILE]: 'User Profile',
  [AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS]: 'User Settings',
  [AuthenticatedRoute.SETTINGS_MONITORING]: 'Asset Monitoring',
  [AuthenticatedRoute.SETTINGS_SUBSCRIPTION]: 'Annual Subscription',
  [AuthenticatedRoute.FILES]: 'Documents',
  [AuthenticatedRoute.INTEGRATIONS]: 'Integrations',
  [AuthenticatedRoute.JOBS]: 'Jobs',
  [AuthenticatedRoute.ASSETS]: 'Assets',
  [AuthenticatedRoute.VULNERABILITIES]: 'Vulnerabilities',
  [AuthenticatedRoute.SEEDS]: 'Seeds',
  [AuthenticatedRoute.LOGOUT]: 'Log Out',
  [AuthenticatedRoute.USERS]: 'Users',
  [AuthenticatedRoute.THREATS]: 'Threats',
  [AuthenticatedRoute.ATTACKS]: 'Attacks',
  [AuthenticatedRoute.VALIDATIONS]: 'Validations',
  [AuthenticatedRoute.TECHNOLOGIES]: 'Technologies',
  [AuthenticatedRoute.AGENTS]: 'Agents',
  [AuthenticatedRoute.CAPABILITIES]: 'Capabilities',
  [AuthenticatedRoute.CUSTOMER_MANAGEMENT]: 'Customer Management',
  [AuthenticatedRoute.RED_TEAM]: 'Red Team',
};

export const riskSeveritySortOrder = [
  RiskSeverity.Critical,
  RiskSeverity.High,
  RiskSeverity.Medium,
  RiskSeverity.Low,
  RiskSeverity.Info,
  RiskSeverity.Exposure,
];

export const surfaceFilters: Record<
  string,
  NonNullable<NodeDetails['filters']>[number]
> = {
  internal: {
    field: '',
    operator: 'AND',
    value: [
      {
        field: '',
        operator: 'OR',
        value: [
          {
            field: 'attackSurface',
            operator: 'IN',
            value: ['internal'],
            reverseOperands: true,
          },
          {
            field: 'private',
            operator: '=',
            value: true,
          },
        ],
      },
      {
        field: 'source',
        operator: '=',
        value: 'account',
        not: true,
      },
    ],
  },
  external: {
    field: '',
    operator: 'AND',
    value: [
      {
        field: 'attackSurface',
        operator: 'IN',
        value: ['external', 'application'],
        reverseOperands: true,
      },
      {
        field: 'source',
        operator: '=',
        value: 'account',
        not: true,
      },
    ],
  },
  cloud: {
    field: 'attackSurface',
    operator: 'IN',
    value: ['cloud'],
    reverseOperands: true,
  },
  repository: {
    field: 'class',
    operator: '=',
    value: ['repository'],
  },
};

export const attackSurfaceOptions: DropdownOption[] = [
  {
    id: 'internal',
    label: 'Internal',
    value: 'internal',
  },
  {
    id: 'external',
    label: 'External',
    value: 'external',
  },
  {
    id: 'cloud',
    label: 'Cloud',
    value: 'cloud',
  },
  {
    id: 'repository',
    label: 'Repository',
    value: 'repository',
  },
];

export const PRODUCTION_ORIGINS = [
  'https://guard.praetorian.com',
  'https://8472.app.guard.praetorian.com',
];
